import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import apiService from "../../utils/configAxios";
import { formatOnlyDate, formattedPrice } from "../../utils";
import { redirectToRaiffeisenPayment } from "../../utils/raiffeisenPayment";
import { fetchMySubscription, fetchSubscribe } from "../../actions/global";
import { SUBSCRIPTION_FEATURE_KEYS } from "../../constants/subscriptionOffer";
import { useGlobalModal } from "../../context/ModalContext";
import SubscriptionPromoCode from "./SubscriptionPromoCode";
import useSubscriptionPromoCode from "../../hooks/useSubscriptionPromoCode";
import useCheckoutReturnRecovery from "../../hooks/useCheckoutReturnRecovery";
import {
  classifySubscriptionPromoTerms,
  formatPromoCurrency,
  SUBSCRIPTION_PROMO_TERM_VARIANTS,
} from "../../utils/subscriptionPromo";

const SubscriptionOffer = ({ mode = "modal", hideModal }) => {
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();
  const { openModal } = useGlobalModal();
  const { userData } = useSelector((state) => state.userReducer);
  const { mySubscription } = useSelector((state) => state.globalReducer);
  const isAuthenticated = !!userData?.firstName;
  const checkoutInFlightRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [planPrice, setPlanPrice] = useState(null);
  const [promoOptIn, setPromoOptIn] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(false);
  const promo = useSubscriptionPromoCode({
    dispatch,
    authenticated: isAuthenticated,
  });

  const recoverCheckoutAfterReturn = useCallback(async () => {
    try {
      await dispatch(fetchMySubscription());
    } finally {
      setLoading(false);
      setCheckingSubscription(false);
    }
  }, [dispatch]);

  useCheckoutReturnRecovery(checkoutInFlightRef, recoverCheckoutAfterReturn);

  const hasActiveSubscription = mySubscription?.status === "ACTIVE";

  useEffect(() => {
    let mounted = true;

    apiService
      .get("/api/subscription/plan")
      .then((response) => {
        if (mounted) {
          setPlanPrice(response?.data?.price ?? null);
        }
      })
      .catch(() => {
        if (mounted) {
          setPlanPrice(null);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    if (!isAuthenticated || mySubscription) {
      setCheckingSubscription(false);
      return () => {
        mounted = false;
      };
    }

    setCheckingSubscription(true);

    dispatch(fetchMySubscription()).finally(() => {
      if (mounted) {
        setCheckingSubscription(false);
      }
    });

    return () => {
      mounted = false;
    };
  }, [dispatch, isAuthenticated, mySubscription]);

  const resolveSubscriptionState = async () => {
    if (!isAuthenticated) {
      return null;
    }

    if (mySubscription) {
      return mySubscription;
    }

    setCheckingSubscription(true);
    try {
      return await dispatch(fetchMySubscription());
    } finally {
      setCheckingSubscription(false);
    }
  };

  const beginCheckout = async () => {
    if (checkoutInFlightRef.current) {
      return;
    }

    if (promo.reportUnapplied()) {
      return;
    }

    const checkoutQuote = promo.quote
      ? Object.freeze({ ...promo.quote })
      : null;
    const checkoutPromoCode = checkoutQuote?.code || "";
    checkoutInFlightRef.current = true;
    setLoading(true);
    let checkoutStarted = false;

    try {
      const currentSubscription = await resolveSubscriptionState();

      if (currentSubscription?.status === "ACTIVE") {
        return;
      }

      const paymentData = await dispatch(
        fetchSubscribe(true, checkoutPromoCode, {
          suppressErrorToast: Boolean(checkoutPromoCode),
          ...(checkoutQuote?.quoteFingerprint
            ? { quoteFingerprint: checkoutQuote.quoteFingerprint }
            : {}),
          ...(checkoutQuote
            ? { quotedInitialAmount: checkoutQuote.initialAmount }
            : {}),
          ...(checkoutQuote?.checkoutInstructionVersion
            ? {
                checkoutInstructionVersion:
                  checkoutQuote.checkoutInstructionVersion,
              }
            : {}),
        })
      );
      checkoutStarted = true;
      redirectToRaiffeisenPayment(paymentData);
    } catch (err) {
      checkoutStarted = false;
      if (checkoutPromoCode) {
        promo.handleCheckoutError(err);
      }
    } finally {
      if (!checkoutStarted) {
        setLoading(false);
        checkoutInFlightRef.current = false;
      }
    }
  };

  const handleCheckoutAfterLogin = async () => {
    setCheckingSubscription(true);

    try {
      const currentSubscription = await dispatch(fetchMySubscription());

      if (currentSubscription?.status === "ACTIVE") {
        return;
      }

      if (promo.appliedCode || promo.hasUnappliedDraft) {
        const eligibility = await promo.apply({ authenticated: true });
        if (!eligibility.ok) {
          return;
        }
      }
    } finally {
      setCheckingSubscription(false);
    }
  };

  const handleSubscribe = async () => {
    if (hasActiveSubscription || checkingSubscription) {
      return;
    }

    if (!isAuthenticated) {
      openModal("login", { onSuccess: handleCheckoutAfterLogin });
      return;
    }

    if (promo.reportUnapplied()) {
      return;
    }

    await beginCheckout();
  };

  const promoTermsVariant = classifySubscriptionPromoTerms(promo.quote);
  const priceLabel =
    promo.quote
      ? formatPromoCurrency(
          promo.quote.initialAmount,
          promo.quote.currency,
          i18n?.language || "sr"
        )
      : planPrice !== null
      ? `${formattedPrice(planPrice)} RSD`
      : t("subscription.modal.priceFallback");
  const promoPeriodKey = {
    [SUBSCRIPTION_PROMO_TERM_VARIANTS.AFFILIATE_MONTHLY]:
      "subscription.modal.affiliatePeriod",
    [SUBSCRIPTION_PROMO_TERM_VARIANTS.FREE_VERIFICATION]:
      "subscription.modal.freePeriod",
  }[promoTermsVariant];
  const pricePeriodLabel = promo.quote
    ? t(promoPeriodKey || "subscription.modal.promoPeriod", {
        count: promo.quote.durationDays,
      })
    : t("subscription.modal.perMonth");
  const subscribeButtonKey =
    promoTermsVariant ===
    SUBSCRIPTION_PROMO_TERM_VARIANTS.AFFILIATE_MONTHLY
      ? "subscription.modal.affiliateSubscribeButton"
      : promoTermsVariant ===
          SUBSCRIPTION_PROMO_TERM_VARIANTS.FREE_VERIFICATION
        ? "subscription.modal.freeVerificationButton"
        : "subscription.modal.subscribeButton";
  const activeUntilLabel = mySubscription?.currentPeriodEnd
    ? t("subscription.modal.activeUntil", {
        date: formatOnlyDate(mySubscription.currentPeriodEnd),
      })
    : null;

  return (
    <div
      className={`subscription-offer subscription-offer-${mode}`}
      data-testid={`subscription-offer-${mode}`}
    >
      {mode === "modal" && (
        <div className="subscribe-modal-title">
          <h1>{t("subscription.modal.title")}</h1>
          <button
            type="button"
            className="cart-modal-close-icon"
            aria-label={t("subscription.modal.close")}
            onClick={hideModal}
          >
            <img src="/images/close.png" alt="" className="img-fluid" />
          </button>
        </div>
      )}

      {mode === "page" && (
        <div className="subscription-offer-page-header">
          <span>{t("subscription.page.eyebrow")}</span>
          <h1>{t("subscription.page.title")}</h1>
          <p>{t("subscription.page.subtitle")}</p>
        </div>
      )}

      <div className="subscribe-modal-content">
        <div className="subscribe-modal-description">
          <p>{t("subscription.modal.description")}</p>
        </div>

        <div className="subscribe-modal-price">
          <span className="subscribe-modal-price-value">{priceLabel}</span>
          <span className="subscribe-modal-price-period">
            / {pricePeriodLabel}
          </span>
        </div>

        <div className="subscribe-modal-features">
          <ul>
            {SUBSCRIPTION_FEATURE_KEYS.map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ul>
        </div>

        <div className="subscribe-modal-testimonial">
          <p className="subscribe-modal-testimonial-quote">
            {t("subscription.modal.testimonial.quote")}
          </p>
          <p className="subscribe-modal-testimonial-author">
            {t("subscription.modal.testimonial.author")}
          </p>
        </div>

        <div className="subscribe-modal-auto-renew subscribe-modal-auto-renew-locked">
          <p>{t("subscription.modal.autoRenewLabel")}</p>
        </div>

        <div className="subscribe-modal-auto-renew">
          <label>
            <input
              type="checkbox"
              checked={promoOptIn}
              onChange={(event) => setPromoOptIn(event.target.checked)}
            />
            {t("subscription.modal.promoLabel")}
          </label>
        </div>

        <SubscriptionPromoCode
          promo={promo}
          idPrefix={`subscription-${mode}`}
          currentPriceAlreadyProminent
          disabled={loading || checkingSubscription}
        />

        <div className="subscribe-modal-submit">
          {hasActiveSubscription && (
            <div className="subscribe-modal-status-note" role="status">
              <p>{t("subscription.modal.activeMessage")}</p>
              {activeUntilLabel && <p>{activeUntilLabel}</p>}
            </div>
          )}
          <button
            type="button"
            className="purple-button"
            onClick={handleSubscribe}
            aria-busy={loading}
            disabled={
              loading ||
              checkingSubscription ||
              promo.isChecking ||
              hasActiveSubscription
            }
          >
            {loading
              ? t("cart.processingPayment")
              : hasActiveSubscription
              ? t("subscription.modal.activeButton")
              : t(subscribeButtonKey)}
          </button>
          <p className="subscribe-modal-legal-note">
            {t(
              promoTermsVariant ===
                SUBSCRIPTION_PROMO_TERM_VARIANTS.FREE_VERIFICATION
                ? "subscription.modal.secureVerification"
                : "subscription.modal.securePayment"
            )}
          </p>
        </div>

      </div>
    </div>
  );
};

export default SubscriptionOffer;
